import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { apiClient } from '@/services/api';
import type { ParticipantBreakdownData } from '@/services/api';
import { isCompleted } from '@/constants/matchStatus';
import { usePageConfig } from '@/contexts/PageConfigContext';
import Error404 from '@/components/Error404';
import ParticipantStatsHeader from '@/components/ParticipantStatsHeader';
import { ContentLayout } from '@/layouts/ContentLayout';
import ParticipantStatsStatsTab from '@/pages/participant-stats/StatsTab';
import { ParticipantStatsContext } from './context';

/**
 * Standalone tabbed layout for the per-participant statistics page
 * (`/tournaments/:tournamentId/leaderboard/:userId/*`).
 *
 * Replaces the previous embedded mode inside TournamentDetailLayout so the
 * tab nav at top shows the participant-scoped tabs (single "Статистика" for
 * now; the array shape is preserved so future tabs can be appended without
 * restructuring).
 *
 * Data: one `useQuery` on `getParticipantBreakdown`; tabs read it via
 * `useParticipantStatsContext()`.
 *
 * Sidebar: on desktop (`≥ lg`) the `<ParticipantStatsHeader>` (accuracy stats
 * card) is rendered in the sidebar. On mobile the sidebar is
 * hidden by CSS, and the tab renders the same card inline at the top of
 * the main column. The `headerInSidebar` context flag tells the tab which
 * side owns the header so it isn't duplicated.
 */
const ParticipantStatsLayout: React.FC = () => {
  const { tournamentId, userId } = useParams<{ tournamentId: string; userId: string }>();
  const { isAuthenticated } = useAuth();
  const isMobile = useBreakpointDown('lg');

  const { data: breakdownEnvelope, isLoading, error } = useQuery({
    queryKey: ['participant-stats', tournamentId, userId],
    queryFn: () => apiClient.getParticipantBreakdown(tournamentId!, userId!),
    enabled: !!tournamentId && !!userId && isAuthenticated,
  });

  const data: ParticipantBreakdownData | undefined = breakdownEnvelope?.data?.data;

  const displayName = React.useMemo(() => {
    if (!data) return '';
    const p = data.participant;
    const full = `${p.first_name || ''} ${p.last_name || ''}`.trim();
    return full || p.username || `User #${p.user_id}`;
  }, [data]);

  // Distribution buckets — derived from the FULL match list. Computed once
  // here; the StatsTab (mobile inline) reads the same result from context.
  const distribution = React.useMemo(() => {
    const empty = { exact: 0, outcome: 0, wrong: 0, pending: 0 };
    if (!data) return empty;
    let exact = 0;
    let outcome = 0;
    let wrong = 0;
    let pending = 0;
    for (const m of data.matches) {
      const breakdown = m.points_breakdown;
      const finished = isCompleted(m.status) && m.actual_score !== null;
      if (!finished || !breakdown) {
        pending += 1;
        continue;
      }
      const isExact = (breakdown.score_points ?? 0) > 0;
      // An exact score is a correct outcome too, so it counts in both buckets.
      if (isExact) exact += 1;
      if (isExact || (breakdown.result_points ?? 0) > 0) {
        outcome += 1;
      } else {
        wrong += 1;
      }
    }
    return { exact, outcome, wrong, pending };
  }, [data]);

  // Page-level chrome (AppLayout top bar): title = participant name.
  // useLayoutEffect inside usePageConfig handles the synchronous commit before paint.
  // While name is empty (first paint before data lands), pass a single space so the header
  // reserves space without showing a placeholder.
  usePageConfig({
    title: displayName || ' ',
    headerSolid: true,
  });

  // Tabs — single "Статистика" tab for now (KPI + distribution + predictions
  // list merged into one page). The `tabs` array shape is preserved so future
  // additions only require pushing a new `{ component, path }` entry here +
  // a child <Route> in App.tsx + a matching option in RouterNavigationSwiper.
  const tabs = React.useMemo(() => ([
    {
      component: ParticipantStatsStatsTab,
      path: `/tournaments/${tournamentId}/leaderboard/${userId}`,
    },
  ]), [tournamentId, userId]);

  if (isLoading) return null;

  if (error) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status === 404 || status === 400) return <Error404 />;
    // Minimal fallback — return null for non-404 errors to keep this layout focused.
    // The shell (AppLayout) still renders with the configured back button.
    return null;
  }

  if (!data) return null;

  const { participant } = data;

  // Desktop: header lives in the sidebar so the main column carries only the
  // filter + match cards. Mobile: sidebar is hidden by CSS — sidebar prop
  // stays null and the tab renders the header inline.
  const sidebar = isMobile ? null : (
    <ParticipantStatsHeader
      totalPredictions={participant.total_predictions}
      correctPredictions={participant.correct_predictions}
      totalPoints={participant.total_points}
      distribution={distribution}
    />
  );

  return (
    <ParticipantStatsContext.Provider
      value={{
        data,
        tournamentId: tournamentId!,
        userId: userId!,
        displayName,
        distribution,
        headerInSidebar: !isMobile,
      }}
    >
      <ContentLayout sidebar={sidebar} tabs={tabs} />
    </ParticipantStatsContext.Provider>
  );
};

export default ParticipantStatsLayout;
